import * as playerRepository from "../repositories/playerRepository";
import { fetchUserProfile, patchUserProfile } from "../repositories/userRepository";
import { ensureUserProfile } from "./userService";
import { ensurePlayerForUser, resolvePlayer } from "./identityService";
import { calculateProfileCompletion } from "../domain/identity/profileCompletion";
import { validatePlayerProfilePatch } from "../domain/identity/validatePlayerProfile";
import { defaultAvatarDataUrl } from "../domain/identity/defaultAvatar";
import { PROFILE_PHOTO_LOCAL_MARKER } from "../domain/identity/playerProfileConstants";
import {
  getLocalProfilePhotoDataUrl,
  hasLocalProfilePhoto,
  removeLocalProfilePhoto,
  saveLocalProfilePhoto,
  compressImageFileForProfilePhoto,
} from "../repositories/localProfilePhotoStorage";
import { uploadEntityThemePhoto, deleteEntityThemePhoto } from "../repositories/cloudMediaStorage";
import { rememberPlayerPhotoUrl } from "../repositories/playerPhotoUrlCache";
import { withMediaCacheBust, friendlyMediaError } from "./media/mediaUrl";
import { isMediaApiConfigured } from "./media/mediaService";
import { profileBootstrapPatch } from "../domain/identity/playerProfileDefaults";
import { rallyAllyUserIdFromEmail } from "../domain/auth/rallyAllyAuthEmail";
import {
  placeholderPlayerNamePatch,
  resolveAccountPlayerName,
} from "../domain/identity/playerNameParts";
import { auth } from "../firebase/config";
import {
  inferProfileFieldGroup,
  trackProfileCompleted,
  trackProfileUpdated,
} from "./analytics/bridges/profileAnalytics";

/** @typedef {import('../domain/identity/playerProfileTypes').PlayerProfilePatch} PlayerProfilePatch */
/** @typedef {import('../domain/identity/playerProfileTypes').PlayerProfileViewModel} PlayerProfileViewModel */
/** @typedef {import('../domain/identity/playerProfileTypes').PlayerWithProfile} PlayerWithProfile */

/**
 * @param {PlayerWithProfile | null | undefined} player
 * @returns {string | null}
 */
export function resolveProfileAvatarUrl(player) {
  if (!player?.playerId) return null;
  const local = getLocalProfilePhotoDataUrl(player.playerId);
  if (local) return local;
  if (
    typeof player.profilePhoto === "string" &&
    player.profilePhoto &&
    player.profilePhoto !== PROFILE_PHOTO_LOCAL_MARKER &&
    (player.profilePhoto.startsWith("http") || player.profilePhoto.startsWith("data:"))
  ) {
    return withMediaCacheBust(player.profilePhoto, player.photoUpdatedAt);
  }
  return defaultAvatarDataUrl(player.displayName || "Player");
}

/**
 * @param {import('../domain/identity/playerTypes').PlayerRecord['createdAt']} createdAt
 * @returns {number | null}
 */
function toMemberSinceMs(createdAt) {
  if (!createdAt) return null;
  if (typeof createdAt === "number") return createdAt;
  if (typeof createdAt.toMillis === "function") return createdAt.toMillis();
  return null;
}

function playerNeedsProfileBootstrap(player) {
  return (
    !player?.profileVersion ||
    player.isProfilePublic === undefined ||
    player.profileCompletedPercentage == null
  );
}

/**
 * @param {string} userId
 * @param {string} [displayNameHint]
 * @returns {Promise<PlayerWithProfile>}
 */
/**
 * Publish this account's RallyAlly id on the player so partner search can tell
 * same names apart. Other players cannot read the private user document.
 * @param {PlayerWithProfile} player
 */
async function withPublicLoginId(player) {
  const loginId = rallyAllyUserIdFromEmail(auth.currentUser?.email || "");
  if (!loginId || !player?.playerId || player.profileExtensions?.loginId === loginId) {
    return player;
  }
  const updated = await playerRepository.updateProfile(player.playerId, {
    profileExtensions: { ...(player.profileExtensions || {}), loginId },
  });
  return /** @type {PlayerWithProfile} */ (updated);
}

export async function ensureMyPlayer(userId, displayNameHint) {
  const preferred = resolveAccountPlayerName(
    displayNameHint,
    auth.currentUser?.email || ""
  );
  let player = await ensurePlayerForUser(userId, preferred);

  /** @type {Record<string, unknown>} */
  const patch = {};
  if (playerNeedsProfileBootstrap(player)) {
    Object.assign(patch, profileBootstrapPatch(player, preferred));
  }
  Object.assign(patch, placeholderPlayerNamePatch(player, preferred));

  if (Object.keys(patch).length > 0) {
    player = /** @type {PlayerWithProfile} */ (
      await playerRepository.updateProfile(player.playerId, patch)
    );
  }

  return withPublicLoginId(player);
}

/**
 * @param {PlayerWithProfile} player
 * @param {{ viewerUserId?: string | null; mode: 'owner' | 'public' }} options
 * @returns {PlayerProfileViewModel}
 */
export function buildProfileViewModel(player, options) {
  const isOwner =
    options.mode === "owner" ||
    (options.viewerUserId && player.userId && player.userId === options.viewerUserId);
  const isPrivate = player.isProfilePublic === false;
  const canViewDetails = isOwner || !isPrivate;

  const hasLocal = hasLocalProfilePhoto(player.playerId);
  const completion = calculateProfileCompletion(player, { hasLocalPhoto: hasLocal });

  if (!canViewDetails) {
    return {
      playerId: player.playerId,
      mode: "public",
      canEdit: false,
      isPrivate: true,
      displayName: "Private player",
      firstName: null,
      lastName: null,
      avatarUrl: defaultAvatarDataUrl("?"),
      city: null,
      country: null,
      bio: null,
      preferredHand: "",
      isProfilePublic: false,
      profileCompletedPercentage: 0,
      completionItems: [],
    };
  }

  return {
    playerId: player.playerId,
    mode: isOwner ? "owner" : "public",
    canEdit: Boolean(isOwner),
    isPrivate,
    displayName: player.displayName,
    firstName: player.firstName ?? null,
    lastName: player.lastName ?? null,
    avatarUrl: resolveProfileAvatarUrl(player),
    city: player.city ?? null,
    country: player.country ?? null,
    countryIso: player.countryIso ?? null,
    state: player.state ?? null,
    club: player.club ?? null,
    dateOfBirth: isOwner ? player.dateOfBirth ?? null : null,
    preferredEvents: Array.isArray(player.preferredEvents) ? player.preferredEvents : [],
    gender: player.gender ?? null,
    contactEmail: player.contactEmail ?? null,
    phoneCountryIso: player.phoneCountryIso ?? null,
    phoneDialCode: player.phoneDialCode ?? null,
    phoneNationalNumber: player.phoneNationalNumber ?? null,
    bio: player.bio ?? null,
    preferredHand: player.preferredHand || "",
    playingCategory: player.playingCategory || "",
    memberSinceMs: toMemberSinceMs(player.createdAt),
    isProfilePublic: player.isProfilePublic !== false,
    profileCompletedPercentage: completion.percentage,
    completionItems: completion.items,
  };
}

/**
 * @param {string} userId
 * @param {string} [displayNameHint]
 */
export async function getMyProfileView(userId, displayNameHint) {
  await ensureUserProfile(userId);
  const player = await ensureMyPlayer(userId, displayNameHint);
  return buildProfileViewModel(player, { viewerUserId: userId, mode: "owner" });
}

/**
 * @param {string} playerId
 * @param {string | null} [viewerUserId]
 */
export async function getPublicProfileView(playerId, viewerUserId = null) {
  const player = await resolvePlayer(playerId);
  if (!player) return null;
  return buildProfileViewModel(/** @type {PlayerWithProfile} */ (player), {
    viewerUserId,
    mode: "public",
  });
}

/**
 * @param {string} userId
 * @param {PlayerProfilePatch} patch
 * @param {string} [displayNameHint]
 */
export async function updateMyProfile(userId, patch, displayNameHint) {
  const player = await ensureMyPlayer(userId, displayNameHint);
  if (player.userId !== userId) {
    await playerRepository.claimPlayer(player.playerId, userId);
  }

  const validated = validatePlayerProfilePatch(patch);
  if (!validated.ok) throw new Error(validated.error);

  const hasLocal = hasLocalProfilePhoto(player.playerId);
  const prevCompletion = player.profileCompletedPercentage ?? 0;
  const merged = { ...player, ...validated.data };
  const completion = calculateProfileCompletion(merged, { hasLocalPhoto: hasLocal });

  const updated = await playerRepository.updateProfile(player.playerId, {
    ...validated.data,
    profileCompletedPercentage: completion.percentage,
  });

  const fieldsUpdatedCount = Object.keys(validated.data).length;
  if (fieldsUpdatedCount > 0) {
    trackProfileUpdated({
      fieldsUpdatedCount,
      fieldGroup: inferProfileFieldGroup(validated.data),
    });
  }
  if (completion.percentage >= 100 && prevCompletion < 100) {
    trackProfileCompleted({
      completionPercent: completion.percentage,
      hasAvatar: hasLocal || Boolean(updated.profilePhoto),
    });
  }

  if (patch.displayName && userId) {
    const userProfile = await fetchUserProfile(userId);
    if (userProfile && userProfile.displayName !== patch.displayName) {
      await patchUserProfile(userId, { displayName: patch.displayName });
    }
  }

  // Keep Arena Players roster labels in sync with the profile name.
  const nameTouched =
    validated.data.firstName !== undefined ||
    validated.data.lastName !== undefined ||
    validated.data.displayName !== undefined;
  if (nameTouched && userId) {
    try {
      const { syncMyRosterDisplayNameInRecentArenas } = await import(
        "./session/syncMyRosterDisplayName"
      );
      await syncMyRosterDisplayNameInRecentArenas(userId);
    } catch (err) {
      console.warn("[profile] roster name sync failed", err?.message || err);
    }
  }

  return buildProfileViewModel(/** @type {PlayerWithProfile} */ (updated), {
    viewerUserId: userId,
    mode: "owner",
  });
}

/**
 * Upload profile photo → R2 → Firestore. Rolls back R2 if Firestore fails.
 * @param {string} userId
 * @param {File} file
 * @param {{ onProgress?: (phase: string, detail?: object) => void }} [opts]
 */
export async function uploadMyProfilePhoto(userId, file, opts = {}) {
  const { onProgress } = opts;
  const player = await ensureMyPlayer(userId);

  if (!isMediaApiConfigured()) {
    const dataUrl = await compressImageFileForProfilePhoto(file);
    saveLocalProfilePhoto(player.playerId, dataUrl);
    rememberPlayerPhotoUrl(player.playerId, dataUrl);
    onProgress?.("saving");
    const view = await updateMyProfile(userId, { profilePhoto: PROFILE_PHOTO_LOCAL_MARKER });
    onProgress?.("complete");
    return view;
  }

  let cloudUrl = null;
  try {
    cloudUrl = await uploadEntityThemePhoto({
      kind: "player",
      entityId: player.playerId,
      file,
      onProgress,
    });
  } catch (e) {
    throw friendlyMediaError(e);
  }

  const photoUpdatedAt = Date.now();
  const storedUrl = withMediaCacheBust(cloudUrl, photoUpdatedAt);
  onProgress?.("saving");
  try {
    try {
      const dataUrl = await compressImageFileForProfilePhoto(file);
      saveLocalProfilePhoto(player.playerId, dataUrl);
    } catch {
      /* optional local cache */
    }
    rememberPlayerPhotoUrl(player.playerId, storedUrl);
    const view = await updateMyProfile(userId, {
      profilePhoto: storedUrl,
      photoUpdatedAt,
    });
    onProgress?.("complete");
    return view;
  } catch (e) {
    try {
      await deleteEntityThemePhoto("player", player.playerId);
    } catch {
      /* best-effort rollback */
    }
    rememberPlayerPhotoUrl(player.playerId, player.profilePhoto || null);
    throw friendlyMediaError(
      e instanceof Error
        ? Object.assign(e, { message: e.message || "Could not save photo" })
        : e
    );
  }
}

/**
 * Remove profile photo from R2 + Firestore; restore default avatar.
 * @param {string} userId
 * @param {{ onProgress?: (phase: string) => void }} [opts]
 */
export async function removeMyProfilePhoto(userId, opts = {}) {
  const { onProgress } = opts;
  const player = await ensureMyPlayer(userId);
  removeLocalProfilePhoto(player.playerId);
  onProgress?.("removing");
  if (isMediaApiConfigured()) {
    try {
      await deleteEntityThemePhoto("player", player.playerId);
    } catch (e) {
      const status = /** @type {{ status?: number }} */ (e)?.status;
      if (status !== 404) throw friendlyMediaError(e);
    }
  }
  rememberPlayerPhotoUrl(player.playerId, null);
  onProgress?.("saving");
  const view = await updateMyProfile(userId, {
    profilePhoto: null,
    photoUpdatedAt: Date.now(),
  });
  onProgress?.("complete");
  return view;
}

export { calculateProfileCompletion };
